// TanStack Query hooks for RFC-0018 favorites (the SPA's first WRITE feature).
//
// `useFavorites` reads `GET /api/favorites` (gated on an authenticated session);
// `useToggleFavorite` is the codebase's FIRST `useMutation` — it stars/unstars via
// the new `apiSend` write transport and, on success, invalidates the favorites
// family plus the affected doc's detail key so `DocDetail.isFavorited` refreshes.
// This follows the connect-repository-modal.tsx `invalidateQueries` precedent
// rather than inventing a new convention.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { FavoriteItem } from '@portal/shared-types';
import { apiGet, apiSend } from '@/lib/api-client';
import { useAuth } from '@/context/auth';
import { projectKeys } from '@/lib/queries/projects';

/**
 * Hierarchical key factory. `all: ['favorites']` is the family root, so a mutation
 * invalidates every favorites query at once (mirrors `projectKeys`).
 */
export const favoriteKeys = {
  all: ['favorites'] as const,
  list: () => [...favoriteKeys.all, 'list'] as const,
};

/**
 * `GET /api/favorites` — the current user's favorites, most-recently-starred first
 * (orphan-pruned server-side). Gated on `isAuthenticated` so it never fires on the
 * login shell. Selects the `data` array off the `{ data }` envelope.
 */
export function useFavorites(): UseQueryResult<FavoriteItem[], Error> {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: favoriteKeys.list(),
    queryFn: () => apiGet<{ data: FavoriteItem[] }>('/api/favorites'),
    enabled: isAuthenticated,
    select: (r) => r.data,
  });
}

/** Input for {@link useToggleFavorite} — the doc plus its CURRENT star state. */
export interface ToggleFavoriteInput {
  repo: string;
  path: string;
  /** The doc's current favorite state; the mutation flips it. */
  isFavorited: boolean;
}

/**
 * Toggle a doc's favorite state — the codebase's first `useMutation`. Stars when
 * currently unfavorited (`POST /api/favorites`), unstars when currently favorited
 * (`DELETE /api/favorites`); both are idempotent server-side. On success it
 * invalidates the favorites family AND the affected `projectKeys.doc(repo, path)`
 * so an open reader's `isFavorited` re-fetches.
 */
export function useToggleFavorite(): UseMutationResult<void, Error, ToggleFavoriteInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ repo, path, isFavorited }: ToggleFavoriteInput) =>
      isFavorited
        ? apiSend<void>('DELETE', '/api/favorites', { repo, path })
        : apiSend<void>('POST', '/api/favorites', { repo, path }),
    onSuccess: (_data, { repo, path }) => {
      queryClient.invalidateQueries({ queryKey: favoriteKeys.all });
      queryClient.invalidateQueries({ queryKey: projectKeys.doc(repo, path) });
    },
  });
}
